import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useSlotGroup,
  useCollectionBinding,
  usePropertyAddressUrl,
  usePropertyField,
  SectionContextProvider,
  EditableToken,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone } from '@fortawesome/free-solid-svg-icons/faPhone';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons/faLocationDot';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';

// ============================================================================
// Local helpers (inlined — the parent theme's PropertyInfoPanel and Footer
// helpers aren't part of `@vibesites/theme-comfy`'s public exports)
// ============================================================================

interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface DayHours {
  label: string;
  hours: string;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};
const DAY_FULL: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

function hasValue(v: string | null | undefined): v is string {
  if (!v) return false;
  const normalized = v.trim().toLowerCase();
  return (
    normalized !== '' &&
    normalized !== 'false' &&
    normalized !== 'null' &&
    normalized !== 'undefined' &&
    normalized !== '0' &&
    normalized !== 'closed'
  );
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function formatTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = /^(\d{1,2}):(\d{2})/.exec(trimmed);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

function formatDisplayPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

function hoursDisplay(entry: OfficeHourEntry | undefined): string {
  if (!entry || !hasValue(entry.open_time) || !hasValue(entry.close_time)) {
    return 'Closed';
  }
  return `${formatTime(entry.open_time)} - ${formatTime(entry.close_time)}`;
}

/**
 * Collapses consecutive days that share the same open window into a single
 * range row (e.g. Mon–Fri, Sat–Sun) so the contact panel stays compact.
 */
function listOfficeHours(raw: OfficeHourEntry[]): DayHours[] {
  const byDay = new Map<string, OfficeHourEntry>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key) && !byDay.has(key)) byDay.set(key, entry);
  }

  const entries = DAY_ORDER.map((key) => ({
    key,
    hours: hoursDisplay(byDay.get(key)),
  }));

  const rows: DayHours[] = [];
  let i = 0;
  while (i < entries.length) {
    let j = i;
    while (j + 1 < entries.length && entries[j + 1].hours === entries[i].hours) {
      j += 1;
    }
    const label =
      j > i
        ? `${DAY_SHORT[entries[i].key]} - ${DAY_SHORT[entries[j].key]}`
        : DAY_FULL[entries[i].key];
    rows.push({ label, hours: entries[i].hours });
    i = j + 1;
  }
  return rows;
}

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone',
  fields: {
    text: {
      label: 'Phone',
      type: 'text',
      defaultField: 'property_call_tracking_phone',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Helpers
// ============================================================================

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `tel:${digits}` : raw;
}

/**
 * Strip a trailing ", USA" / " USA" / country-code variant so the displayed
 * address stays on a single tight line (e.g. "1020 Woodlane Rd, Beverly, NJ
 * 08010" instead of "…08010, USA"). Editors can still override the full text
 * via the address slot group.
 */
function stripCountrySuffix(raw: string): string {
  return raw
    .replace(/[,\s]+(USA|United States(?: of America)?)\.?\s*$/i, '')
    .trim();
}

// ============================================================================
// Component
// ============================================================================

interface ContactFormProps {
  /**
   * Unique prefix so each page's overrides are stored independently. Falls
   * back to the canonical slot name so the component works standalone and on
   * the Contact Us page (which sets no prefix).
   */
  tokenPrefix?: string;
  /**
   * Default form block to render when the section has no override. The Contact
   * Us page uses the lead form; the Schedule A Tour page passes the tour
   * scheduler.
   */
  defaultFormBlock?: string;
}

export function ContactForm({ tokenPrefix, defaultFormBlock }: ContactFormProps = {}) {
  useComfyCtaStyles();

  const prefix = tokenPrefix || 'contactform';
  const formBlockPath = `${prefix}.form_block`;
  const talkLabelPath = `${prefix}.talk_label`;
  const talkBlurbPath = `${prefix}.talk_blurb`;
  const hoursLabelPath = `${prefix}.hours_label`;
  const officeLabelPath = `${prefix}.office_label`;
  const addressPath = `${prefix}.address`;
  const phonePath = `${prefix}.phone`;

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: defaultFormBlock || 'multihub-lead-form',
  });

  const addressLink = useSlotGroup(addressPath, addressLinkDef);
  const phoneLink = useSlotGroup(phonePath, phoneLinkDef);

  const propertyAddressUrl = usePropertyAddressUrl();
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as
    | string
    | null
    | undefined;
  const propertyPhone = usePropertyField('property_phone') as
    | string
    | null
    | undefined;

  const officeHours = useCollectionBinding<OfficeHourEntry>(`${prefix}.office_hours`, {
    collection: 'office_hours',
  });
  const dayHours = officeHours.length > 0 ? listOfficeHours(officeHours) : [];

  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const addressText = stripCountrySuffix(addressLink.text || '');
  const phoneHref =
    toTelUrl(phoneLink.url) || toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);
  const phoneDisplay =
    formatDisplayPhone(phoneLink.text) ||
    formatDisplayPhone(callTrackingPhone) ||
    formatDisplayPhone(propertyPhone) ||
    '555-555-5555';

  return (
    <section className="cf-section">
      <div className="cf-container">
        <div className="cf-grid">
          {/* ── Left column: Phone / Hours / Address (icon-stacked) ── */}
          <div className="cf-info">
            {/* Phone */}
            <div className="cf-info-block">
              <FontAwesomeIcon icon={faPhone} className="cf-info-icon" aria-hidden="true" />
              {phoneHref ? (
                <EditableSlotGroup groupPath={phonePath} definition={phoneLinkDef}>
                  <a
                    href={phoneHref}
                    target={phoneLink._target || '_self'}
                    className="dniphone dniphonehref cf-info-phone"
                  >
                    {phoneDisplay}
                  </a>
                </EditableSlotGroup>
              ) : (
                <span className="cf-info-phone">{phoneDisplay}</span>
              )}
              {/* Hidden heading slot so the "Leasing Office" copy remains editable
                  even though this layout doesn't render a visible label. */}
              <RichTextContent
                path={officeLabelPath}
                as="span"
                className="cf-visually-hidden"
              />
            </div>

            {/* Office Hours */}
            <div className="cf-info-block">
              <FontAwesomeIcon icon={faClock} className="cf-info-icon" aria-hidden="true" />
              <RichTextContent
                path={hoursLabelPath}
                as="span"
                className="cf-visually-hidden"
              />
              {dayHours.length > 0 ? (
                <dl className="cf-hours">
                  {dayHours.map((entry, i) => (
                    <div key={i} className="cf-hours-row">
                      <dt className="cf-hours-day">{entry.label}</dt>
                      <dd className="cf-hours-time">{entry.hours}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="cf-empty">Hours coming soon.</p>
              )}
            </div>

            {/* Address */}
            {addressText && (
              <div className="cf-info-block">
                <FontAwesomeIcon
                  icon={faLocationDot}
                  className="cf-info-icon"
                  aria-hidden="true"
                />
                <EditableSlotGroup groupPath={addressPath} definition={addressLinkDef}>
                  <a
                    href={addressHref}
                    target={addressLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="cf-info-address"
                  >
                    {addressText}
                  </a>
                </EditableSlotGroup>
              </div>
            )}
          </div>

          {/* ── Right column: Blurb + Lead Form ── */}
          <div className="cf-form-col">
            {/* Hidden heading slot preserves editability of the "Let's Talk"
                copy without duplicating the blurb it introduces. */}
            <RichTextContent
              path={talkLabelPath}
              as="span"
              className="cf-visually-hidden"
            />
            <RichTextContent
              path={talkBlurbPath}
              defaultField="property_contactus_blurb"
              as="div"
              className="cf-talk-blurb"
            />
            <div className="cf-form-wrap">
              <EditableToken path={formBlockPath} as="div">
                {formSlot ? (
                  <SectionContextProvider sectionId={formSlot.sectionId}>
                    <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                  </SectionContextProvider>
                ) : (
                  <div className="cf-form-empty-card">
                    <p className="cf-form-empty">No form block selected</p>
                  </div>
                )}
              </EditableToken>
            </div>
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — static canonical paths so the source-level
          linter can match each schema key. The visible wrappers above use the
          per-instance dynamic paths built from `prefix`. These never render. */}
      <EditableToken path="contactform.form_block" as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <RichTextContent path="contactform.talk_label" as="span" className="hidden" style={{ display: 'none' }} />
      <RichTextContent path="contactform.talk_blurb" as="span" className="hidden" style={{ display: 'none' }} />
      <RichTextContent path="contactform.hours_label" as="span" className="hidden" style={{ display: 'none' }} />
      <RichTextContent path="contactform.office_label" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableSlotGroup groupPath="contactform.address" definition={addressLinkDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="contactform.phone" definition={phoneLinkDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>

      <style>{`
        .cf-section {
          background: var(--vibe-color-background, #ffffff);
          padding: 3rem 1.5rem;
        }
        @media (min-width: 768px) {
          .cf-section { padding: 5rem 2rem; }
        }
        .cf-container {
          max-width: 80rem;
          margin: 0 auto;
        }
        .cf-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
          align-items: start;
        }
        @media (min-width: 900px) {
          .cf-grid {
            grid-template-columns: 1fr 1fr;
            gap: 4rem;
            /* Vertical divider between the two columns (image reference). */
            position: relative;
          }
          .cf-grid::before {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            left: 50%;
            width: 1px;
            background: var(--vibe-color-border, rgba(0, 0, 0, 0.15));
            transform: translateX(-50%);
          }
        }

        /* Screen-reader-only helper for retained editable headings. */
        .cf-visually-hidden {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }

        /* ── Left column: contact info (phone / hours / address) ── */
        .cf-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2.5rem;
          text-align: center;
          color: var(--vibe-color-text, #111827);
          padding: 0.5rem 0;
        }
        @media (min-width: 900px) {
          .cf-info { gap: 3rem; }
        }
        .cf-info-block {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          width: 100%;
          max-width: 26rem;
        }
        .cf-info-icon {
          font-size: 1.5rem;
          color: var(--vibe-color-text, #111827);
        }
        .cf-info-phone {
          font-family: var(--vibe-font-heading);
          font-size: 1.75rem;
          font-weight: 400;
          letter-spacing: 0.01em;
          color: var(--vibe-color-text, #111827);
          text-decoration: none;
          transition: color 0.2s ease;
        }
        @media (min-width: 768px) {
          .cf-info-phone { font-size: 2rem; }
        }
        .cf-info-phone:hover,
        .cf-info-phone:focus-visible {
          color: var(--vibe-color-primary, #b28c59);
        }
        .cf-info-address {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.5;
          color: var(--vibe-color-text, #111827);
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .cf-info-address:hover,
        .cf-info-address:focus-visible {
          color: var(--vibe-color-primary, #b28c59);
        }

        /* Hours list: full day names sorted Mon→Sun in a spaced-apart row
           (day left, time right). */
        .cf-hours {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          margin: 0;
          width: 100%;
          max-width: 22rem;
        }
        .cf-hours-row {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.4;
          color: var(--vibe-color-text, #111827);
        }
        @media (min-width: 768px) {
          .cf-hours-row { font-size: 1rem; }
        }
        .cf-hours-day,
        .cf-hours-time {
          margin: 0;
        }

        /* ── Right column: intro blurb + form ── */
        .cf-form-col {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          padding: 0.5rem 0;
        }
        .cf-talk-blurb {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.6;
          color: var(--vibe-color-text, #111827);
          margin: 0;
        }
        .cf-talk-blurb p { margin: 0 0 0.75rem; }
        .cf-talk-blurb p:last-child { margin-bottom: 0; }
        .cf-form-wrap {
          width: 100%;
        }
        /* The lead-form block owns the whole panel background/heading — hide
           the block's own "Contact Us" title so it doesn't duplicate the
           column intro above. Scoped to a direct-child <h3> so the nested
           success-state heading is unaffected. */
        .cf-form-wrap .mh-lead-form-card > h3 {
          display: none;
        }
        /* Mirror the Footer's Apply Now / Resident Portal CTA (footer-cta-btn):
           full-width secondary-color pill with the heading font, 10px radius,
           and the same title-case label + hover surface swap. The block paints
           its buttons with inline styles, so these overrides use !important
           to win. */
        .cf-form-wrap .mh-lead-form-submit-button,
        .cf-form-wrap .mh-lead-form-success-cta {
          display: flex !important;
          width: 100% !important;
          box-sizing: border-box !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 1.1rem 1.5rem !important;
          font-family: var(--vibe-font-heading, var(--vibe-font-body, sans-serif)) !important;
          font-size: 1.1rem !important;
          font-weight: 500 !important;
          letter-spacing: normal !important;
          text-transform: none !important;
          text-align: center !important;
          color: var(--vibe-color-text-on-secondary, #ffffff) !important;
          background: var(--vibe-color-secondary, #c9a961) !important;
          border: none !important;
          border-radius: 10px !important;
          text-decoration: none !important;
          transition: background-color 0.25s ease, color 0.25s ease, opacity 0.25s ease !important;
        }
        .cf-form-wrap .mh-lead-form-submit-button:hover,
        .cf-form-wrap .mh-lead-form-success-cta:hover,
        .cf-form-wrap .mh-lead-form-success-cta:focus {
          background: var(--comfy-cta-bg-hover, var(--vibe-color-surface, #f5f5f5)) !important;
          color: var(--comfy-cta-fg-hover, #1a1a1a) !important;
        }
        .cf-form-empty-card {
          background: var(--vibe-color-background, #fff);
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
        }

        .cf-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms', 'Integrations'],
    },
    'contactform.talk_label': {
      label: "Let's Talk Heading",
      type: 'richtext',
      defaultValue: "<p>Let's Talk</p>",
    },
    'contactform.talk_blurb': {
      label: "Let's Talk Blurb",
      type: 'richtext',
      defaultField: 'property_contactus_blurb',
      defaultValue:
        '<p>Give us a call or fill out the form to find out how you can start living at <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
    'contactform.hours_label': {
      label: 'Office Hours Heading',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'contactform.office_label': {
      label: 'Leasing Office Heading',
      type: 'richtext',
      defaultValue: '<p>Leasing Office</p>',
    },
  },
  slotGroups: {
    'contactform.address': addressLinkDef,
    'contactform.phone': phoneLinkDef,
  },
  collectionBindings: {
    'contactform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
